import ClearIcon from '@mui/icons-material/Clear';
import { Box, FormControl, IconButton, MenuItem, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { DateInput } from '../../ui/input/DateInput';
import type { DateFilterValue, DateOp, DateRangeFilterValue, FilterOp } from '../filterTypes';
import { BETWEEN, DATE_OPERATOR_OPTIONS } from '../filterTypes';
import type { FilterFieldProps } from '../FilterField.types';
import { FieldLabel, FieldError } from '../FilterFieldHelpers';
import { validateFilterValue } from '../filterValidation';
import { textFieldSx } from '../styles/filterColumnSx';
import '../styles/filterPanel.css';

export function DateFilterInput({
  def,
  value,
  onChange,
  onClear,
  variant = 'panel',
  label,
  op = 'eq',
  onOpChange,
  operatorSelectWidth,
}: FilterFieldProps) {
  const theme = useTheme();
  // Caller may override the width; otherwise use the shared theme token.
  const opSelectWidth = operatorSelectWidth ?? theme.filters.operatorSelectWidth;
  const { t } = useTranslation('common');
  const isColumn = variant === 'column';

  const isRange = value?.kind === 'dateRange';
  const range = isRange ? (value as DateRangeFilterValue) : undefined;
  const single = value?.kind === 'date' ? (value as DateFilterValue).value : '';
  const currentValue = isRange ? '' : single;
  const hasValue = currentValue !== '';

  // Emit a plain `date` value, or clear the filter when the box is emptied.
  const emitSingle = (next: string, nextOp: FilterOp) => {
    if (!next) onClear();
    else onChange({ kind: 'date', value: next, op: nextOp });
  };

  // Shared clear affordance — same tokenised styling for the embedded (column)
  // and stand-alone (panel / range) placements.
  const clearButton = (
    <IconButton size="small" onClick={onClear} className="filter-clear-btn">
      <ClearIcon className="filter-clear-icon" />
    </IconButton>
  );

  // Column (quick) filter stays a single date — no operator, no range.
  if (isColumn) {
    return (
      <DateInput
        fullWidth
        value={single}
        onChange={v => emitSingle(v, 'eq')}
        endAdornment={hasValue ? clearButton : undefined}
        sx={textFieldSx(theme)}
      />
    );
  }

  // Switch operator; preserve the entered date when moving to/from `between`.
  const handleOpSelect = (next: DateOp) => {
    if (next === BETWEEN) {
      onChange({
        kind: 'dateRange',
        from: isRange ? range!.from : single,
        to: isRange ? range!.to : '',
      });
    } else if (isRange) {
      // Preserve whichever bound was entered — an open-ended range may hold only `to`.
      const preserved = range!.from || range!.to;
      if (preserved) onChange({ kind: 'date', value: preserved, op: next });
      else onClear();
    } else {
      onOpChange?.(next);
    }
  };

  const emitRange = (from: string, to: string) => {
    if (!from && !to) onClear();
    else onChange({ kind: 'dateRange', from, to });
  };

  // All date operators, incl. `between` (range): the backend ANDs the two gte/lte
  // params (see common/filters/apply-kysely-filters), so the range filter is live.
  const operatorOptions = DATE_OPERATOR_OPTIONS;

  const operatorSelect = (
    <Select
      value={isRange ? BETWEEN : op}
      onChange={e => handleOpSelect(e.target.value as DateOp)}
      className="filter-select"
      inputProps={{ 'aria-label': t('table.filter.dateOperator') }}
    >
      {operatorOptions.map(o => (
        <MenuItem key={o.value} value={o.value} className="filter-menu-item-sm">
          {t(`table.filter.dateOp.${o.key}`)}
        </MenuItem>
      ))}
    </Select>
  );

  // `between`: the cramped 1fr grid column can't fit operator + two date boxes on
  // one flex row, so stack — operator on its own line, the two date boxes sharing
  // the line below (each flex:1, minWidth:0) with a "→" separator. This keeps both
  // date boxes on the same line regardless of the column width.
  if (isRange) {
    // Incohérence de plage (date de fin < date de début) : message inline. Le
    // filtre n'est pas envoyé au backend (appendFilterParams skip via le même
    // validateur). Les min/max natifs restent un garde-fou visuel du picker.
    const rangeError = validateFilterValue(def, value);
    return (
      <Box className="filter-field">
        {label && <FieldLabel>{label}</FieldLabel>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              {operatorSelect}
            </FormControl>
            {clearButton}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <DateInput
              value={range!.from}
              max={range!.to || undefined}
              onChange={v => emitRange(v, range!.to)}
              className="filter-input"
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Box
              component="span"
              sx={{ flexShrink: 0, color: 'var(--fp-label-color)', fontSize: '0.875rem' }}
            >
              →
            </Box>
            <DateInput
              value={range!.to}
              min={range!.from || undefined}
              onChange={v => emitRange(range!.from, v)}
              className="filter-input"
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Box>
          {rangeError && (
            <FieldError>{t(`table.filter.error.${rangeError.key}`)}</FieldError>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box className="filter-field">
      {label && <FieldLabel>{label}</FieldLabel>}
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'stretch', height: '100%' }}>
        <FormControl size="small" sx={{ width: opSelectWidth, flexShrink: 0 }}>
          {operatorSelect}
        </FormControl>

        <DateInput
          fullWidth
          value={currentValue}
          onChange={v => emitSingle(v, op)}
          className="filter-input"
        />

        {hasValue && clearButton}
      </Box>
    </Box>
  );
}
